// Home loans (finance table, per-user) — P6a. Pure normalisers + CRUD; the
// maths lives in loan-engine.js.
//
// Stored rows:
// - `loan#<uuid>` — `{ name, lender, settlementDate, repaymentDay, portions:
//   [{ id, name, type: 'variable'|'fixed', principal, termYears, offset }],
//   refinancedFrom (a loan itemId | null), closedOn (date | null), notes }`.
//   Interest is charged (and the repayment taken) on `repaymentDay` each month.
//   A refinance = the old loan `closedOn` the new one's settlement date, the
//   new one `refinancedFrom` it.
// - `loan-event#<loanUuid>:<uuid>` — `{ date, type, portionId, rate, amount,
//   fromOffset, note }`: `rate` (% a year, from that day — the repayment is
//   worked out from it), `extra` (a lump sum off the loan — `fromOffset` =
//   taken out of the offset), `redraw` (added to the loan), `balance` (a
//   statement balance — the model resets to it). Fees aren't tracked (record
//   one as an offset withdrawal if it matters).
// - `loan-offset#<uuid>` — the ONE household offset account (continuous across
//   refinances): `{ date, kind: 'balance'|'deposit'|'withdrawal', amount, note }`.
// - `loan-contribution#<uuid>` — a contribution PLAN, like a fund deposit plan:
//   `{ from, amounts: { [personId]: { amount, period } } }`, in force until the
//   next plan; `period` null = on my paydays, else weekly / fortnightly /
//   monthly from `from` (fund-deposits.js `cadenceDates`). `ME` = me.
// - `loan-people#` — the household people who contribute (besides me):
//   `{ people: [{ id, name }] }`.
//
// Money = `{ amount, currency: 'AUD' }` (integer cents).

import { financeStore } from './store.js';
import { isoDate, toYmd } from './fy.js';
import { readMoney } from './money.js';
import { DEPOSIT_PERIODS } from './fund-deposits.js';

export const LOAN_PREFIX = 'loan#';
export const LOAN_EVENT_PREFIX = 'loan-event#';
export const LOAN_OFFSET_PREFIX = 'loan-offset#';
export const LOAN_CONTRIBUTION_PREFIX = 'loan-contribution#';
export const LOAN_PEOPLE_ID = 'loan-people#';
export const PORTION_TYPES = Object.freeze(['variable', 'fixed']);
export const LOAN_EVENT_TYPES = Object.freeze(['rate', 'extra', 'redraw', 'balance']);
export const OFFSET_KINDS = Object.freeze(['balance', 'deposit', 'withdrawal']);
/** The person id for me in contribution plans. */
export const ME = 'me';
const DEFAULT_TERM_YEARS = 30;

const text = (v) => (typeof v === 'string' ? v.trim() : '');
const aud = (v, opts = {}) => readMoney(v, { currencies: ['AUD'], min: 0, ...opts });
function dateOrNull(v) {
  if (!v) return null;
  try {
    return isoDate(v);
  } catch {
    return null;
  }
}
const randomUuid = () => crypto.randomUUID();

function normalisePortion(p, i) {
  const type = PORTION_TYPES.includes(p?.type) ? p.type : 'variable';
  const term = Number(p?.termYears);
  return {
    id: text(p?.id) || `p${i + 1}`,
    name: text(p?.name) || (type === 'fixed' ? 'Fixed' : 'Variable'),
    type,
    principal: aud(p?.principal) ?? { amount: 0, currency: 'AUD' },
    termYears: Number.isFinite(term) && term > 0 && term <= 40 ? term : DEFAULT_TERM_YEARS,
    offset: typeof p?.offset === 'boolean' ? p.offset : type === 'variable',
  };
}

/** A loan's stored shape (defensive: bad fields fall back to defaults). */
export function normaliseLoan(l) {
  const settlementDate = dateOrNull(l?.settlementDate) ?? isoDate(new Date());
  const day = Math.round(Number(l?.repaymentDay));
  const portions = (Array.isArray(l?.portions) ? l.portions : []).map(normalisePortion);
  const seen = new Set();
  const unique = portions.filter((p) => !seen.has(p.id) && seen.add(p.id));
  return {
    name: text(l?.name) || 'Home loan',
    lender: text(l?.lender),
    settlementDate,
    repaymentDay: day >= 1 && day <= 31 ? day : toYmd(settlementDate).d,
    portions: unique.length ? unique : [normalisePortion({}, 0)],
    refinancedFrom: text(l?.refinancedFrom) || null,
    closedOn: dateOrNull(l?.closedOn),
    notes: text(l?.notes),
  };
}

/** An event's stored shape, or null when invalid. */
export function normaliseLoanEvent(e) {
  const date = dateOrNull(e?.date);
  const type = LOAN_EVENT_TYPES.includes(e?.type) ? e.type : null;
  if (!date || !type) return null;
  const out = { date, type, portionId: text(e?.portionId) || null, note: text(e?.note) };
  if (type === 'rate') {
    const rate = Number(e?.rate);
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) return null;
    return { ...out, rate };
  }
  const amount = aud(e?.amount);
  if (!amount) return null;
  if (type === 'extra') return { ...out, amount, fromOffset: e?.fromOffset !== false };
  return { ...out, amount };
}

/** An offset entry's stored shape, or null when invalid. */
export function normaliseOffsetEntry(e) {
  const date = dateOrNull(e?.date);
  const kind = OFFSET_KINDS.includes(e?.kind) ? e.kind : null;
  const amount = aud(e?.amount);
  if (!date || !kind || !amount) return null;
  return { date, kind, amount, note: text(e?.note) };
}

/** A contribution plan's stored shape (zero amounts dropped; none = paused). */
export function normaliseContributionPlan(p) {
  const from = dateOrNull(p?.from) ?? isoDate(new Date());
  const amounts = {};
  for (const [id, value] of Object.entries(p?.amounts ?? {})) {
    const personId = text(id);
    const amount = aud(value?.amount);
    if (!personId || !amount || amount.amount <= 0) continue;
    const period = DEPOSIT_PERIODS.includes(value?.period) ? value.period : null;
    amounts[personId] = { amount, period };
  }
  return { from, amounts };
}

/**
 * The household people list's stored shape: `{ people: [{ id, name }] }`
 * (names required, ids unique; never includes me — `ME` is implicit).
 */
export function normaliseLoanPeople(row, genId = randomUuid) {
  const people = [];
  for (const p of Array.isArray(row?.people) ? row.people : []) {
    const name = text(p?.name);
    if (!name) continue;
    let id = text(p?.id) || genId();
    if (id === ME || people.some((x) => x.id === id)) id = genId();
    people.push({ id, name });
  }
  return { people };
}

/** A person's display name (`ME` → "You"; unknown → "Someone else"). */
export function personName(people, personId) {
  if (personId === ME) return 'You';
  return people.find((p) => p.id === personId)?.name ?? 'Someone else';
}

// ─── Storage ─────────────────────────────────────────────────────────────────

const withId = (row, normalised) => ({ itemId: row.itemId, ...normalised });
const uuidOf = (loanItemId) => loanItemId.slice(LOAN_PREFIX.length);
const eventPrefixFor = (loanItemId) => `${LOAN_EVENT_PREFIX}${uuidOf(loanItemId)}:`;
const byDate = (a, b) => a.date.localeCompare(b.date);

/** The loan an event row belongs to (its `loan#…` item id). */
export function loanRefOf(eventItemId) {
  const rest = eventItemId.slice(LOAN_EVENT_PREFIX.length);
  return `${LOAN_PREFIX}${rest.slice(0, rest.indexOf(':'))}`;
}

/** Everything the loan pages need, normalised and sorted. */
export async function loadLoanData(doc, userId) {
  const [[loans, events, offset, plans], peopleRow] = await Promise.all([
    Promise.all(
      [LOAN_PREFIX, LOAN_EVENT_PREFIX, LOAN_OFFSET_PREFIX, LOAN_CONTRIBUTION_PREFIX].map((p) =>
        financeStore.listItems(doc, userId, p),
      ),
    ),
    financeStore.getItem(doc, userId, LOAN_PEOPLE_ID),
  ]);
  const keep = (rows, fn) =>
    rows.flatMap((r) => {
      const n = fn(r);
      return n ? [withId(r, n)] : [];
    });
  return {
    loans: keep(loans, normaliseLoan).sort((a, b) =>
      a.settlementDate.localeCompare(b.settlementDate),
    ),
    events: keep(events, normaliseLoanEvent)
      .map((e) => ({ ...e, loanRef: loanRefOf(e.itemId) }))
      .sort(byDate),
    offset: keep(offset, normaliseOffsetEntry).sort(byDate),
    contributions: keep(plans, normaliseContributionPlan).sort((a, b) =>
      a.from.localeCompare(b.from),
    ),
    people: normaliseLoanPeople(peopleRow ?? {}).people,
  };
}

/** Save the household people list (returns the normalised list). */
export async function saveLoanPeople(doc, userId, people) {
  const stored = normaliseLoanPeople({ people });
  await financeStore.updateItem(doc, userId, LOAN_PEOPLE_ID, stored);
  return stored.people;
}

async function add(doc, userId, prefix, stored, itemId) {
  const row = await financeStore.addItem(doc, userId, prefix, stored, itemId);
  return withId(row, stored);
}

function required(stored, what) {
  if (!stored) throw new Error(`Invalid ${what}`);
  return stored;
}

export function createLoan(doc, userId, loan, genId = randomUuid) {
  return add(doc, userId, LOAN_PREFIX, normaliseLoan(loan), `${LOAN_PREFIX}${genId()}`);
}

export async function createLoanEvent(doc, userId, loanItemId, event, genId = randomUuid) {
  const stored = required(normaliseLoanEvent(event), 'loan event');
  return add(doc, userId, LOAN_EVENT_PREFIX, stored, `${eventPrefixFor(loanItemId)}${genId()}`);
}

export async function createOffsetEntry(doc, userId, entry, genId = randomUuid) {
  const stored = required(normaliseOffsetEntry(entry), 'offset entry');
  return add(doc, userId, LOAN_OFFSET_PREFIX, stored, `${LOAN_OFFSET_PREFIX}${genId()}`);
}

export function createContributionPlan(doc, userId, plan, genId = randomUuid) {
  const stored = normaliseContributionPlan(plan);
  return add(
    doc,
    userId,
    LOAN_CONTRIBUTION_PREFIX,
    stored,
    `${LOAN_CONTRIBUTION_PREFIX}${genId()}`,
  );
}

const NORMALISERS = {
  [LOAN_PREFIX]: [normaliseLoan, 'loan'],
  [LOAN_EVENT_PREFIX]: [normaliseLoanEvent, 'loan event'],
  [LOAN_OFFSET_PREFIX]: [normaliseOffsetEntry, 'offset entry'],
  [LOAN_CONTRIBUTION_PREFIX]: [normaliseContributionPlan, 'contribution plan'],
};

/** Update any loan row (loan / event / offset entry / contribution plan). */
export async function updateLoanItem(doc, userId, itemId, value) {
  const prefix = Object.keys(NORMALISERS)
    .sort((a, b) => b.length - a.length)
    .find((p) => itemId.startsWith(p));
  if (!prefix) throw new Error(`Not a loan item: ${itemId}`);
  const [fn, what] = NORMALISERS[prefix];
  const stored = required(fn(value), what);
  await financeStore.updateItem(doc, userId, itemId, stored);
  return { itemId, ...stored };
}

/** Delete a loan with its events; other rows delete on their own. */
export async function deleteLoan(doc, userId, loanItemId) {
  const events = await financeStore.listItems(doc, userId, eventPrefixFor(loanItemId));
  await Promise.all(events.map((e) => financeStore.deleteItem(doc, userId, e.itemId)));
  await financeStore.deleteItem(doc, userId, loanItemId);
}

export function deleteLoanItem(doc, userId, itemId) {
  return financeStore.deleteItem(doc, userId, itemId);
}
